import React from 'react';
import { HowItWorksConnections } from '../components/HowItWorksConnections';
import { BenefitsBento } from '../components/BenefitsBento';
import { NarrativeSection } from '../components/NarrativeSection';
import { WallOfLove } from '../components/WallOfLove';
import { GettingStartedSteps } from '../components/GettingStartedSteps';
import { ClosingCTA } from '../components/ClosingCTA';
import { AudienceSwitch } from '../components/AudienceSwitch';
import { TwelveTabProblem } from '../components/TwelveTabProblem';
import { ConnectedAppsPillar } from '../components/alt/ConnectedAppsPillar';
import { BrainFlowExperiment } from '../components/alt/BrainFlowExperiment';
import { CoWorkerPillar } from '../components/alt/CoWorkerPillar';
import { PlatformPillar } from '../components/alt/PlatformPillar';
import { ArchitectureWidget } from '../components/alt/ArchitectureWidget';
import { ConnectedAppsTable } from './Alt';

/**
 * One section shell for every product-screen beat, so the four pillars share a
 * rhythm: same container width, same vertical measure, alternating cream and
 * white grounds. The older home components each carry their own <section>, so
 * only the transplanted pillars need it.
 */
function PillarSection({
  id,
  tone,
  children
}: {
  id?: string;
  tone: 'cream' | 'white';
  children: React.ReactNode;
}) {
  return <section id={id} className={`scroll-mt-24 border-b border-[#E7E0D4] py-24 md:py-32 ${tone === 'cream' ? 'bg-[#FAF8F4]' : 'bg-white'}`} data-id="element-9712" data-mp="2mz34n9q2osb0">
      <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-9713" data-mp="2d6p1ccjcjdmq">{children}</div>
    </section>;
}

// The home page now tells the product story with the real product screens: the
// hero composer, what it is, the problem it removes, then the four pillars in
// the order the argument runs — connect the tools, let what they know compound,
// take that intelligence to where people already work, and build on it.
export function Home() {
  return <>
      <HowItWorksConnections mode="hero" data-id="element-9714" data-mp="1u8usbyi7x24c" />
      {/* "What Giga can do" overview — the bento leads the page right after the
       hero so cold ad/UGC traffic gets the answer ("what is this, and it
       actually does the work") before anything else. */}
      <BenefitsBento data-id="element-9715" data-mp="1an2q4ivgkut5" />
      {/* Problem statement as a CONTRAST beat — "the world without Giga" (11
       tabs / context-switch tax). Leading with the answer, not the problem, is
       intentional for traffic that arrives pre-sold on the pain. */}
      <TwelveTabProblem data-id="element-9716" data-mp="23c06uhk0ujjs" />

      {/* Pillar 1 — Connected Apps. The comparison table sits inside this
       section rather than standing alone later on the page: it is an argument
       about where connections should live, so it belongs with the map that
       shows them. It replaces the older ToolComparison beat. */}
      <PillarSection id="connected-apps" tone="cream" data-id="element-9717" data-mp="2ma2sqnh9w0dd">
        <ConnectedAppsPillar data-id="element-9718" data-mp="2m9ayph3bqap7" />
        <div className="mt-20 md:mt-28" data-id="element-9719" data-mp="2m89jr1j6t427">
          <ConnectedAppsTable data-id="element-9720" data-mp="2r12o3oaquadr" />
        </div>
      </PillarSection>

      {/* Pillar 2 — Company Brain. Replaces the older ContextGraph and
       MemoryCarousel beats, which were two separate takes on memory; this one
       shows the sources, the stores, and a question reading all three. */}
      <PillarSection id="company-brain" tone="white" data-id="element-9721" data-mp="oennrjepk24n">
        <BrainFlowExperiment data-id="element-9722" data-mp="og8e0wtzoywp" />
      </PillarSection>

      {/* Pillar 3 — AI Co Worker. Replaces LivesInSlack, which showed Slack
       only; Teams, Claude Code and ChatGPT are now named surfaces. */}
      <PillarSection id="co-worker" tone="cream" data-id="element-9723" data-mp="3sugdrf8o1bya">
        <CoWorkerPillar data-id="element-9724" data-mp="3nu0wwdp6dmbk" />
      </PillarSection>

      {/* Pillar 4 — Workspace. The primitives the platform is built from. */}
      <PillarSection id="workspace" tone="white" data-id="element-9725" data-mp="5p7hsgk97hgu">
        <PlatformPillar data-id="element-9726" data-mp="otbp427qhlg" />
      </PillarSection>

      {/* Modularity — adopt Giga in pieces, alongside the AI tools already in
       use. The diagram's group panels are cream, so this one needs white. */}
      <PillarSection tone="white" data-id="element-9727" data-mp="1c485jyz4ktcc">
        <ArchitectureWidget data-id="element-9728" data-mp="1c36sjoyjtwrz" />
      </PillarSection>

      {/* Setup, then who it is for. */}
      <GettingStartedSteps data-id="element-9729" data-mp="27f6jnfycsp5k" />
      <AudienceSwitch data-id="element-9730" data-mp="27fzna820myjh" />

      {/* Finale energy ramp: calm cream social proof (WallOfLove) → bold orange
       founder conviction (NarrativeSection) → dark confident close. */}
      <WallOfLove data-id="element-9731" data-mp="2hahsxiqzt45x" />
      <NarrativeSection data-id="element-9732" data-mp="2he5i65pen4eu" />
      <ClosingCTA data-id="element-9733" data-mp="2maw2544byk5j" />
    </>;
}